import type { ReactNode } from 'react';
import { cn } from '@/shared/lib/utils';
import { ArchivedAlert } from './ArchivedAlert';

export interface ArchivableGuardProps {
  /** Když je `true`, zobrazí banner a obsah znepřístupní pro editaci (read-only). */
  isArchived: boolean;
  children: ReactNode;
  /** Přepis titulku banneru. */
  bannerTitle?: ReactNode;
  /** Přepis popisu banneru. */
  bannerDescription?: ReactNode;
  /** Volitelná akce v banneru (mimo zablokovaný obsah – zůstává klikatelná). */
  bannerAction?: ReactNode;
  className?: string;
  /** Třída pro wrapper editovatelného obsahu (fieldset). */
  contentClassName?: string;
}

/**
 * Sdílený wrapper pro entity s příznakem archivace.
 *
 * Když je entita archivovaná, zobrazí nad obsahem <see cref="ArchivedAlert"/> a obsah obalí do
 * <c>&lt;fieldset disabled&gt;</c>, čímž znemožní editaci všech formulářových prvků uvnitř
 * (vstupy, tlačítka, selecty). Banner i jeho akce zůstávají mimo zablokovanou oblast.
 *
 * Sjednocuje IsArchived UX a je znovupoužitelný napříč entitami (projekt, série, …).
 */
export function ArchivableGuard({
  isArchived,
  children,
  bannerTitle,
  bannerDescription,
  bannerAction,
  className,
  contentClassName,
}: ArchivableGuardProps) {
  return (
    <div className={cn('flex flex-col gap-4', className)} data-testid="archivable-guard">
      {isArchived ? (
        <ArchivedAlert title={bannerTitle} description={bannerDescription} action={bannerAction} />
      ) : null}
      <fieldset
        disabled={isArchived}
        aria-disabled={isArchived || undefined}
        className={cn('m-0 min-w-0 border-0 p-0', isArchived && 'opacity-60', contentClassName)}
      >
        {children}
      </fieldset>
    </div>
  );
}
